import { useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import styled from 'styled-components';

import { getPathFromNode } from 'utils/helpers';

import GraphButton, { GraphButtonMetric } from '../../GraphButton';
import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardSubH1,
} from '../../SectionCard';
import DownloadButton from '../../Button/DownloadButton';
import { LinkTracked } from '../../Link';
import Loading from '../../Loading';
import SemanticList from '../../SemanticList';
import { SoftButton } from '../../Button';
import { GraphNavSortSelect as SortSelect } from '../../Input/SortSelect';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import sortDataByKey from 'utils/sortDataByKey';
import { useSort } from 'utils/hooks';

const List = styled(SemanticList)`
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-column-gap: 18px;
    grid-row-gap: 16px;

    & > * {
        display: flex;
        flex-direction: column;
    }

    @media (min-width: ${({ theme }) => theme.bp.sm}px) {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    @media (min-width: ${({ theme }) => theme.bp.md}px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: ${({ theme }) => theme.bp.lg}px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`;

function NavItems({
    sortedItems,
    sortOption,
    maxSortMetric,
    minVisible = 8,
    parentId,
    primaryMetric,
    secondaryMetric,
    defaultSecondaryMetric,
}) {
    const [numVisible, setNumVisible] = useState(minVisible);

    if (!sortedItems) return `No subcategories to display`;

    return (
        <>
            <nav>
                <List>
                    {sortedItems.slice(0, numVisible).map((item) => {
                        return (
                            <GraphButton
                                as={LinkTracked}
                                key={item.id}
                                to={getPathFromNode(
                                    item,
                                    parentId,
                                    sortOption.dataSource,
                                )}
                                mixpanelContext="GraphNav"
                                percentage={
                                    !!maxSortMetric
                                        ? (item[sortOption.sortBy] /
                                              maxSortMetric) *
                                          100
                                        : 0
                                }
                                primaryMetric={
                                    <GraphButtonMetric
                                        metric={item[primaryMetric.sortBy]}
                                        label={primaryMetric.label}
                                        formatFn={primaryMetric.format}
                                    />
                                }
                                secondaryMetric={
                                    <GraphButtonMetric
                                        metric={item[secondaryMetric.sortBy]}
                                        formatFn={secondaryMetric.format}
                                        isSigned={secondaryMetric.isSigned}
                                        label={secondaryMetric.label}
                                        showLabel={
                                            secondaryMetric ===
                                            defaultSecondaryMetric
                                        }
                                    />
                                }
                            >
                                {item.name}
                            </GraphButton>
                        );
                    })}
                </List>
            </nav>
            <div css="display: flex; align-items: center;">
                {sortedItems.length > minVisible ? (
                    <SoftButton
                        IconLeft={FiPlus}
                        size="sm"
                        onClick={() =>
                            setNumVisible(numVisible + minVisible * 3)
                        }
                        isDisabled={numVisible >= sortedItems.length}
                    >
                        Show more subcategories
                    </SoftButton>
                ) : null}
                {numVisible > minVisible ? (
                    <>
                        <span
                            css={`
                                border-left: solid 1px
                                    ${({ theme }) => theme.gray_5};
                                min-height: 16px;
                                margin: 0px 12px;
                            `}
                        />
                        <SoftButton
                            size="sm"
                            onClick={() => setNumVisible(minVisible)}
                        >
                            Hide All
                        </SoftButton>
                    </>
                ) : null}
            </div>
        </>
    );
}

const getCSVColumnsFromSortOptions = (sortOptions) => {
    const columns = new Map();
    columns.set('name');
    sortOptions.forEach(({ sortBy, label }) => {
        columns.set(sortBy, label);
    });
    return Array.from(columns.entries()).map(([key, label]) => ({
        key,
        label,
    }));
};

function GraphNav({
    name,
    filename = 'graph nav',
    isLoading,
    data = null,
    sortOptions,
    primaryMetric,
    defaultSecondaryMetric,
    week,
    year,
    categoryId,
}) {
    const formattedItems = useMemo(() => {
        if (!data) return data;
        return data.filter(
            (item) =>
                item.name &&
                (item[primaryMetric.sortBy] ||
                    item[primaryMetric.sortBy] === 0 ||
                    item.views ||
                    item.views === 0),
        );
    }, [data, primaryMetric]);

    const { setSortOption, sortOption, sortedData, maxSortMetric } = useSort(
        formattedItems,
        sortDataByKey,
        primaryMetric,
    );

    const secondaryMetric =
        !sortOption ||
        !sortOption.sortBy ||
        sortOption.sortBy === primaryMetric.sortBy
            ? defaultSecondaryMetric
            : sortOption;

    return (
        <SectionCard>
            <SectionCardHeading css="margin-top: -5px;">
                <SectionCardH1 css="align-content: center;">
                    Top Subcategories
                </SectionCardH1>
                <SectionCardSubH1>
                    <span>
                        for{' '}
                        <span css="text-transform: capitalize;">{name}</span>{' '}
                        by&nbsp;
                        <SortSelect
                            options={sortOptions}
                            value={sortOption}
                            onChange={(selected) => setSortOption(selected)}
                            week={week}
                            year={year}
                            mixpanelContext="GraphNavSortSelect"
                        />
                    </span>
                </SectionCardSubH1>
                <div css="margin-left: auto;">
                    <DownloadButton
                        disabled={!data || data.length < 1 || isLoading}
                        fetching={isLoading}
                        onClick={() =>
                            downloadItemsAsCSV(
                                filename,
                                sortedData,
                                getCSVColumnsFromSortOptions(sortOptions),
                            )
                        }
                        mixpanelMeta={{ contentKey: 'GraphNav' }}
                    />
                </div>
            </SectionCardHeading>
            {isLoading ? (
                <Loading />
            ) : (
                <NavItems
                    sortedItems={sortedData}
                    sortOption={sortOption}
                    maxSortMetric={maxSortMetric}
                    sortOptions={sortOptions}
                    parentId={categoryId}
                    primaryMetric={primaryMetric}
                    secondaryMetric={secondaryMetric}
                    defaultSecondaryMetric={defaultSecondaryMetric}
                />
            )}
        </SectionCard>
    );
}

export default GraphNav;
